import { ACCOUNT_API_COMPANIES, SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { Company } from 'domain/company/Company';
import { Provider } from 'domain/subscription/interfaces/Subscription';
import { useGetOnMount } from 'hooks/useGetOnMount';
import { UnsavedChangesModal } from './UnsavedChangesModal/UnsavedChangesModal';
import useSubmit, { POST } from 'hooks/useSubmit';
import { RequestStatus } from 'types/RequestStatus';

import {
	FunctionComponent,
	ReactNode,
	createContext,
	useContext,
	useEffect,
	useState,
} from 'react';
import { useBlockNavigationOnUnsavedChanges } from 'hooks/useBlockNavigationOnUnsavedChanges';

export type ProviderBlacklistContextType = {
	allCompanies?: Company[];
	selectedProvider: Provider | null;
	pendingProvider: Provider | null;
	addedCompanies: Company[];
	removedCompanies: Company[];
	isLoading: boolean;
	requestProviderChange: (provider: Provider) => void;
	setAddedCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
	setRemovedCompanies: React.Dispatch<React.SetStateAction<Company[]>>;
	resetChanges: () => void;
	submitChanges: () => Promise<void>;
};

export const ProviderBlacklistContext =
	createContext<ProviderBlacklistContextType | null>(null);

const ProviderBlacklistContextProvider: FunctionComponent<{
	children: ReactNode;
}> = ({ children }) => {
	const [allCompanies, setAllCompanies] = useState<Company[]>();
	const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
		null,
	);
	const [pendingProvider, setPendingProvider] = useState<Provider | null>(
		null,
	);
	const [addedCompanies, setAddedCompanies] = useState<Company[]>([]);
	const [removedCompanies, setRemovedCompanies] = useState<Company[]>([]);

	const { data: companyOptions } = useGetOnMount<Company[]>(
		`${ACCOUNT_API_COMPANIES}/GetCompanies`,
	);

	const [updateProviderBlacklist, updateProviderBlacklistStatus] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/UpdateProviderBlacklist`,
		POST,
		{
			messageOnSuccess: 'Provider blacklist updated successfully',
			messageOnError: 'Error updating provider blacklist',
			showApiError: true,
		},
	);

	const isLoading = updateProviderBlacklistStatus === RequestStatus.LOADING;

	useEffect(() => {
		if (!companyOptions) return;

		setAllCompanies(companyOptions);
	}, [companyOptions]);

	const resetChanges = () => {
		setAddedCompanies([]);
		setRemovedCompanies([]);
	};

	const submitChanges = async () => {
		if (!selectedProvider) return;

		const response = await updateProviderBlacklist({
			providerId: selectedProvider.id,
			added: addedCompanies.map((c) => c.id),
			removed: removedCompanies.map((c) => c.id),
		});

		if (response.success) {
			resetChanges();
		}
	};

	const requestProviderChange = (provider: Provider) => {
		const isDirty =
			addedCompanies.length > 0 || removedCompanies.length > 0;

		if (selectedProvider && isDirty) {
			setPendingProvider(provider);
		} else {
			setSelectedProvider(provider);
			setAddedCompanies([]);
			setRemovedCompanies([]);
		}
	};

	const confirmProviderChange = () => {
		if (pendingProvider) {
			setSelectedProvider(pendingProvider);
			setPendingProvider(null);
			resetChanges();
		}
	};

	const cancelProviderChange = () => {
		setPendingProvider(null);
	};

	const isDirty = addedCompanies.length > 0 || removedCompanies.length > 0;

	const {
		isBlocking: showNavigationModal,
		handleDiscardChanges,
		handleKeepEditing,
		handleSaveAndProceed,
	} = useBlockNavigationOnUnsavedChanges({
		when: isDirty,
		onDiscard: resetChanges,
		onSave: submitChanges,
	});

	const showModal = showNavigationModal || !!pendingProvider;

	const handleDiscard = () => {
		if (pendingProvider) {
			resetChanges();
			confirmProviderChange();
		} else {
			handleDiscardChanges();
		}
	};

	const handleKeepEdit = () => {
		if (pendingProvider) {
			cancelProviderChange();
		} else {
			handleKeepEditing();
		}
	};

	const handleSave = async () => {
		if (pendingProvider) {
			await submitChanges();
			confirmProviderChange();
		} else {
			await handleSaveAndProceed();
		}
	};

	return (
		<ProviderBlacklistContext.Provider
			value={{
				allCompanies,
				selectedProvider,
				pendingProvider,
				addedCompanies,
				removedCompanies,
				isLoading,
				requestProviderChange,
				setAddedCompanies,
				setRemovedCompanies,
				resetChanges,
				submitChanges,
			}}
		>
			{children}
			<UnsavedChangesModal
				isOpen={showModal}
				isLoading={isLoading}
				onDiscard={handleDiscard}
				onKeepEditing={handleKeepEdit}
				onSave={handleSave}
				addedCompanies={addedCompanies}
				removedCompanies={removedCompanies}
				providerName={selectedProvider?.name || ''}
			/>
		</ProviderBlacklistContext.Provider>
	);
};

export function useProviderBlacklistContext() {
	const context = useContext(ProviderBlacklistContext);

	if (!context) {
		throw new Error(
			'useProviderBlacklistContext must be used within the ProviderBlacklistContextProvider',
		);
	}

	return context;
}

export default ProviderBlacklistContextProvider;
